import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import axios from 'axios';
import DeleteModal from '../../components/DeleteModal';
import MediaSelector from '../../components/MediaSelector';
import '../../styles/components/forms.css';
export default function NewActivity() {
    const [formData, setFormData] = useState({
        activityName: '',
        programId: [],
        shortDescription: '',
        description: '',
        voicingLevel: 'some',
        levelId: '',
        stepId: '',
        environmentId: '',
        isPublic: false,
        isFree: true,
        isLoop: false,
        minutes: ''
    });
    const [programs, setPrograms] = useState([]);
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [existingTags, setExistingTags] = useState([]);
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [msg, setMsg] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({
        activityName: '',
        programId: [],
        shortDescription: '',
        description: '',
        voicingLevel: 'some',
        tags: [],
        levelId: '',
        stepId: '',
        environmentId: '',
        isPublic: true,
        isFree: false,
        isLoop: false,
        minutes: '',
        audioUrl: '',
        videoUrl: ''
    });
    const [editTagInput, setEditTagInput] = useState('');
    const [editTagSuggestions, setEditTagSuggestions] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [levels, setLevels] = useState([]);
    const [steps, setSteps] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [audioUrl, setAudioUrl] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [audioPlayers, setAudioPlayers] = useState({});
    const [playingStates, setPlayingStates] = useState({});

    const truncateText = (text, maxLength) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.slice(0, maxLength) + '...';
    };

    const fetchActivities = async () => {
        try {
            const activitiesResponse = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities`,
                { withCredentials: true }
            );
            setActivities(activitiesResponse.data);
        } catch (error) {
            console.error('Error fetching activities:', error);
            setError('Kunne ikke hente aktiviteter');
        }
    };

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const [programsResponse, tagsResponse, levelsResponse, stepsResponse, environmentsResponse] = await Promise.all([
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/programs`,
                    { withCredentials: true }
                ),
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/activities/tags`,
                    { withCredentials: true }
                ),
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/levels`,
                    { withCredentials: true }
                ),
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/steps`,
                    { withCredentials: true }
                ),
                axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/environments`,
                    { withCredentials: true }
                )
            ]);

            setPrograms(programsResponse.data);
            setExistingTags(tagsResponse.data);
            setLevels(levelsResponse.data);
            setSteps(stepsResponse.data);
            setEnvironments(environmentsResponse.data);
            await fetchActivities();
        } catch (err) {
            console.error('Error fetching initial data:', err);
            setError('Kunne ikke laste nødvendige data. Vennligst prøv igjen.' + err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, [msg]);

    const handleEdit = (activity) => {
        const programIds = activity.idprograms ? 
            (typeof activity.idprograms === 'string' ? 
                activity.idprograms.split(',').map(id => parseInt(id)) : 
                [activity.idprograms]) : 
            [];

        // Use full URLs for preset so video/audio preview and MediaSelector work (including SAS tokens)
        const audioUrl = activity.norwegianURL || activity.norwegianUrl || '';
        const videoUrl = activity.videoURL || activity.videoUrl || '';

        setEditingId(activity.idactivity);
        setEditData({
            activityName: activity.name || '',
            programId: programIds,
            shortDescription: activity.short_description || '',
            description: activity.description || '',
            voicingLevel: activity.voicing_level || 'some',
            tags: activity.tags ? activity.tags.split(',') : [],
            levelId: activity.intensity_zone_idintensity_zone || '',
            stepId: activity.steps_idsteps || '',
            environmentId: activity.environment_idenvironment || '',
            isPublic: activity.isPublic === 1,
            isFree: activity.isFree === 1,
            isLoop: activity.isLoop === 1,
            minutes: activity.minutes || '',
            audioUrl: audioUrl,
            videoUrl: videoUrl
        });
    };

    const handleUpdate = async (id) => {
        // Validate that either audio or video file is selected
        if (!editData.audioUrl && !editData.videoUrl) {
            setError('Enten lydfil eller videofil er påkrevd for å oppdatere en aktivitet');
            return;
        }
        
        try {
            const response = await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`,
                {
                    name: editData.activityName,
                    shortDescription: editData.shortDescription,
                    description: editData.description,
                    voicingLevel: editData.voicingLevel,
                    tags: editData.tags,
                    idintensity_zone: editData.levelId,
                    idsteps: editData.stepId,
                    idenvironment: editData.environmentId || null,
                    isPublic: editData.isPublic ? 1 : 0,
                    isFree: editData.isFree ? 1 : 0,
                    isLoop: editData.isLoop ? 1 : 0,
                    minutes: editData.minutes,
                    norwegianUrl: editData.audioUrl,
                    englishUrl: editData.audioUrl,
                    videoUrl: editData.videoUrl
                },
                { withCredentials: true }
            );

            if (response.status === 200) {
                setEditingId(null);
                setMsg("Aktivitet oppdatert");
                setEditData({
                    activityName: '',
                    programId: [],
                    shortDescription: '',
                    description: '',
                    voicingLevel: 'some',
                    tags: [],
                    levelId: '',
                    stepId: '',
                    environmentId: '',
                    isPublic: false,
                    isFree: true,
                    isLoop: false,
                    minutes: '',
                    audioUrl: '',
                    videoUrl: ''
                });
                await fetchActivities();
            }
        } catch (error) {
            console.error('Error updating activity:', error);
            setError('Kunne ikke oppdatere aktivitet. Vennligst prøv igjen.');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'programId') {
            const selectedOptions = Array.from(e.target.selectedOptions).map(option => option.value);
            setFormData(prev => ({
                ...prev,
                [name]: selectedOptions
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleTagInput = (e) => {
        const value = e.target.value;
        setTagInput(value);
        
        if (value.trim()) {
            const filtered = existingTags
                .filter(tag => tag.name.toLowerCase().includes(value.toLowerCase()))
                .map(tag => tag.name);
            setSuggestions(filtered);
        } else {
            setSuggestions([]);
        }
    };

    const addTag = (tagName) => {
        if (!tags.includes(tagName) && tagName.trim()) {
            setTags([...tags, tagName.trim()]);
        }
        setTagInput('');
        setSuggestions([]);
    };

    const removeTag = (tagToRemove) => {
        setTags(tags.filter(tag => tag !== tagToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate that either audio or video file is selected
        if (!audioUrl && !videoUrl) {
            setError('Enten lydfil eller videofil er påkrevd for å opprette en aktivitet');
            return;
        }
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities`,
                {
                    ...formData,
                    isPublic: formData.isPublic ? 1 : 0,
                    isFree: formData.isFree ? 1 : 0,
                    isLoop: formData.isLoop ? 1 : 0,
                    tags: tags,
                    audioUrl: audioUrl,
                    videoUrl: videoUrl,
                    voicingLevel: formData.voicingLevel,
                    norwegianUrl: audioUrl,
                    englishUrl: audioUrl,
                    environmentId: formData.environmentId || null
                },
                { withCredentials: true }
            );

            if (response.status === 201) {
                setFormData({ 
                    activityName: '', 
                    programId: [], 
                    description: '',
                    shortDescription: '',
                    voicingLevel: 'some',
                    levelId: '',
                    stepId: '',
                    environmentId: '',
                    isPublic: false,
                    isFree: true,
                    isLoop: false,
                    minutes: ''
                });
                setTags([]);
                setTagInput('');
                setVideoUrl('');
                setAudioUrl('');
                setMsg(response.data.message);
            }
        } catch (error) {
            console.error('Error creating activity:', error);
            setError('Kunne ikke opprette aktivitet. Vennligst prøv igjen.');
        }
    };

    const handleEditTagInput = (e) => {
        const value = e.target.value;
        setEditTagInput(value);
        
        if (value.trim()) {
            const filtered = existingTags
                .filter(tag => tag.name.toLowerCase().includes(value.toLowerCase()))
                .map(tag => tag.name);
            setEditTagSuggestions(filtered);
        } else {
            setEditTagSuggestions([]);
        }
    };

    const addEditTag = (tagName) => {
        if (!editData.tags.includes(tagName) && tagName.trim()) {
            setEditData({
                ...editData,
                tags: [...editData.tags, tagName.trim()]
            });
        }
        setEditTagInput('');
        setEditTagSuggestions([]);
    };

    const handleDelete = async (id) => {
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/activities/${id}`,
                { withCredentials: true }
            );
            
            if (response.status === 200) {
                setDeleteConfirm(null);
                setMsg("Aktivitet slettet");
                // Update the activities list by filtering out the deleted activity
                setActivities(activities.filter(activity => activity.idactivity !== id));
            }
        } catch (error) {
            console.error('Error deleting activity:', error);
            setError('Kunne ikke slette aktivitet. Vennligst prøv igjen.');
        }
    };

    const handlePlayAudio = (activityId, url, language) => {
        const playerKey = `${activityId}-${language}`;
        
        // If already playing, pause it
        if (playingStates[playerKey]) {
            audioPlayers[playerKey].pause();
            setPlayingStates(prev => ({
                ...prev,
                [playerKey]: false
            }));
            return;
        }

        // Pause any other playing audio
        Object.entries(audioPlayers).forEach(([key, player]) => {
            if (key !== playerKey && playingStates[key]) {
                player.pause();
                setPlayingStates(prev => ({
                    ...prev,
                    [key]: false
                }));
            }
        });

        // Play the selected audio
        let audio = audioPlayers[playerKey];
        if (!audio) {
            audio = new Audio(url);
            audio.addEventListener('ended', () => {
                setPlayingStates(prev => ({
                    ...prev,
                    [playerKey]: false
                }));
            });
            setAudioPlayers(prev => ({
                ...prev,
                [playerKey]: audio
            }));
        }
        
        audio.play();
        setPlayingStates(prev => ({
            ...prev,
            [playerKey]: true
        }));
    };

    useEffect(() => {
        return () => {
            // Cleanup audio players on unmount
            Object.values(audioPlayers).forEach(player => {
                player.pause();
            });
        };
    }, [audioPlayers]);

    if (loading) {
        return <div className="loading">Laster...</div>;
    }

    if (error) {
        // return <div className="error-message">{error}</div>;
    }

    return (
        <div className="page-container">
            <div className="page-header-stack">
                <Link to="/dashboard" className="back-button">
                    <i className="fas fa-arrow-left"></i> Tilbake til hovedsiden
                </Link>
                <h1>Opprett ny aktivitet</h1>
            </div>
            <form className="form-container" onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="activityName">Aktivitetsnavn</label>
                    <input
                        type="text"
                        id="activityName"
                        name="activityName"
                        value={formData.activityName}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="form-group">
                    <label htmlFor="shortDescription">Kort beskrivelse</label>
                    <textarea
                        id="shortDescription"
                        name="shortDescription"
                        value={formData.shortDescription}
                        onChange={handleChange}
                        className="form-textarea"
                        rows="2"
                        maxLength="150"
                        placeholder="Kort beskrivelse (maks 150 tegn)"
                    />
                    {/* <small className="character-count">
                        {formData.shortDescription.length}/150 characters
                    </small> */}
                </div>
                <div className="form-group">
                    <label htmlFor="description">Full beskrivelse</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="form-textarea"
                        rows="4"
                        placeholder="Detaljert beskrivelse..."
                    />
                </div>
                <div className="form-group">
                    {/* <label htmlFor="programId">Programs</label> */}
                    <div className="selected-programs">
                        {formData.programId.map(id => {
                            const program = programs.find(p => p.idprograms === parseInt(id));
                            return program ? (
                                <span key={id} className="selected-program-tag">
                                    {program.name}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                programId: prev.programId.filter(pId => pId !== id)
                                            }));
                                        }}
                                        className="tag-remove"
                                    >
                                        ×
                                    </button>
                                </span>
                            ) : null;
                        })}
                    </div>
                    {/* <select
                        id="programId"
                        name="programId"
                        value={formData.programId}
                        onChange={handleChange}
                        multiple
                        className="multi-select"
                    >
                        {programs && programs.map(program => (
                            <option key={program.idprograms} value={program.idprograms}>
                                {program.name}
                            </option>
                        ))}
                    </select> */}
                    {/* <small className="select-hint">
                        <i className="fas fa-info-circle"></i> 
                        {navigator.platform.includes('Mac') 
                            ? 'Hold Command (⌘) to select multiple programs' 
                            : 'Hold Ctrl to select multiple programs'}
                    </small> */}
                </div>

                <div className="form-group">
                    <label htmlFor="tags">Tagger</label>
                    <div className="tags-input-container">
                        <div className="tags-list">
                            {tags.map(tag => (
                                <span key={tag} className="tag">
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => removeTag(tag)}
                                        className="tag-remove"
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                        <input
                            type="text"
                            value={tagInput}
                            onChange={handleTagInput}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    addTag(tagInput);
                                }
                            }}
                            placeholder="Legg til tagger..."
                        />
                        {suggestions.length > 0 && (
                            <div className="tag-suggestions">
                                {suggestions.map(suggestion => (
                                    <div
                                        key={suggestion}
                                        className="tag-suggestion"
                                        onClick={() => addTag(suggestion)}
                                    >
                                        {suggestion}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-group">
                    <label htmlFor="levelId">Intensitetsnivå</label>
                    <select
                        id="levelId"
                        name="levelId"
                        value={formData.levelId}
                        onChange={handleChange}
                        className="form-select"
                    >
                        <option value="">Velg et nivå</option>
                        {levels.map(level => (
                            <option 
                                key={level.idintensity_zone} 
                                value={level.idintensity_zone}
                                style={{
                                    backgroundColor: level.fargekode_bakgrunn,
                                    color: level.fargekode_tekst
                                }}
                            >
                                {level.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="stepId">Steg</label>
                    <select
                        id="stepId"
                        name="stepId"
                        value={formData.stepId}
                        onChange={handleChange}
                        className="form-select"
                    >
                        <option value="">Velg et steg</option>
                        {steps.map(step => (
                            <option 
                                key={step.idsteps} 
                                value={step.idsteps}
                            >
                                {step.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label htmlFor="environmentId">Miljø</label>
                    <select
                        id="environmentId"
                        name="environmentId"
                        value={formData.environmentId}
                        onChange={handleChange}
                        className="form-select"
                    >
                        <option value="">Velg et miljø</option>
                        {environments.map(env => (
                            <option 
                                key={env.idenvironment} 
                                value={env.idenvironment}
                            >
                                {env.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-group toggle-group">
                    <label className="toggle-label">
                        <span className="toggle-text">Publisert aktivitet</span>
                        <i className="fas fa-info-circle tooltip-icon" 
                        data-tooltip="Publiserte aktiviteter er synlige for alle brukere. Private aktiviteter er kun synlige for administratorer.">
                        </i>
                        <div className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.isPublic}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    isPublic: e.target.checked
                                })}
                            />
                            <span className="toggle-slider"></span>
                        </div>
                    
                    </label>
                 
                    <label className="toggle-label">
                        <span className="toggle-text">Gratis aktivitet</span>
                        <i className="fas fa-info-circle tooltip-icon" 
                           data-tooltip="Gratis aktiviteter er tilgjengelige for alle brukere. Ikke-gratis aktiviteter krever et abonnement.">
                        </i>
                        <div className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.isFree}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    isFree: e.target.checked
                                })}
                            />
                            
                            <span className="toggle-slider"></span>
                        </div>
                      
                    </label>

                    <label className="toggle-label">
                        <span className="toggle-text">Gjentakende aktivitet</span>
                        <i className="fas fa-info-circle tooltip-icon" 
                           data-tooltip="Gjentakende aktiviteter kan gjentas kontinuerlig under økten.">
                        </i>
                        <div className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={formData.isLoop}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    isLoop: e.target.checked
                                })}
                            />
                            <span className="toggle-slider"></span>
                        </div>
                    </label>
                </div>

                <div className="form-group">
                    <label htmlFor="minutes">Minutter</label>
                    <input
                        type="number"
                        id="minutes"
                        name="minutes"
                        value={formData.minutes}
                        onChange={handleChange}
                        className="form-input"
                    />
                </div>

                <div className="form-group">
                    <label>Lydfil: <span className="required">*</span></label>
                    <MediaSelector 
                        onSelect={(url) => setAudioUrl(url)}
                        selectedUrl={audioUrl}
                    />
                    {!audioUrl && !videoUrl && (
                        <small className="error-text">Enten lydfil eller videofil er påkrevd</small>
                    )}
                </div>

                <div className="form-group">
                    <label>Videofil: <span className="required">*</span></label>
                    <MediaSelector 
                        onSelect={(url) => setVideoUrl(url)}
                        selectedUrl={videoUrl}
                        folder="video"
                        accept="video/*"
                    />
                    {!audioUrl && !videoUrl && (
                        <small className="error-text">Enten lydfil eller videofil er påkrevd</small>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="voicingLevel">Stemmenivå</label>
                    <select
                        id="voicingLevel"
                        name="voicingLevel"
                        value={formData.voicingLevel}
                        onChange={handleChange}
                        className="form-select"
                    >
                        <option value="no">Ingen stemme</option>
                        <option value="some">Litt stemme</option>
                        <option value="consistent">Konsistent stemme</option>
                    </select>
                </div>

                <button 
                    type="submit" 
                    className="submit-button"
                    disabled={!audioUrl && !videoUrl}
                >
                    Opprett aktivitet
                </button>
            </form>
            {msg && <div className="success-message">{msg}</div>}

            {/* Activities List */}
            <div className="activities-list">
                <h2>Eksisterende aktiviteter</h2>
                <div className="list-container">
                    {activities.length > 0 ? (
                        activities.map((activity) => (
                            <div key={activity.idactivity} className="list-item">
                                <div className="activity-info">
                                    {editingId === activity.idactivity ? (
                                        <div className="edit-form">
                                            {/* Row 1 */}
                                            <div className="edit-form-row">
                                                <div className="edit-form-col">
                                                    <label>Aktivitetsnavn</label>
                                                    <input
                                                        type="text"
                                                        value={editData.activityName}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            activityName: e.target.value
                                                        })}
                                                        className="edit-input"
                                                    />
                                                </div>
                                                {/* <div className="edit-form-col">
                                                    <label>Program</label>
                                                    <select
                                                        value={editData.programId}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            programId: e.target.value
                                                        })}
                                                        className="edit-select"
                                                    >
                                                        {programs.map(program => (
                                                            <option key={program.idprograms} value={program.idprograms}>
                                                                {program.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div> */}
                                            </div>

                                            {/* Row 2 */}
                                            <div className="edit-form-row">
                                                <div className="edit-form-col">
                                                    <label>Nivå</label>
                                                    <select
                                                        value={editData.levelId}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            levelId: e.target.value
                                                        })}
                                                        className="edit-select"
                                                    >
                                                        <option value="">{activity.level_name || "Velg et nivå"}</option>
                                                        {levels.map(level => (
                                                            <option key={level.idintensity_zone} value={level.idintensity_zone}>
                                                                {level.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="edit-form-col">
                                                    <label>Steg</label>
                                                    <select
                                                        value={editData.stepId === null ? "null" : (editData.stepId || '')}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            stepId: e.target.value === "null" ? null : e.target.value
                                                        })}
                                                        className="edit-select"
                                                    >
                                                        <option value="null">Velg et steg</option>
                                                        {steps.map(step => (
                                                            <option 
                                                                key={step.idsteps} 
                                                                value={step.idsteps}
                                                            >
                                                                {step.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* Row 3 */}
                                            <div className="edit-form-row">
                                                <div className="edit-form-col">
                                                    <label>Miljø</label>
                                                    <select
                                                        value={editData.environmentId}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            environmentId: e.target.value
                                                        })}
                                                        className="edit-select"
                                                    >
                                                        <option value="">{activity.environment_name || "Velg et miljø"}</option>
                                                        {environments.map(env => (
                                                            <option key={env.idenvironment} value={env.idenvironment}>
                                                                {env.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="edit-form-col">
                                                    <label>Varighet (minutter)</label>
                                                    <input
                                                        type="number"
                                                        value={editData.minutes}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            minutes: e.target.value
                                                        })}
                                                        min="1"
                                                        className="edit-input"
                                                        placeholder="Skriv varighet i minutter"
                                                    />
                                                </div>
                                            </div>

                                            {/* Full width items */}
                                            <div className="edit-form-row">
                                                <div className="edit-form-col">
                                                    <label>Kort beskrivelse</label>
                                                    <textarea
                                                        value={editData.shortDescription}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            shortDescription: e.target.value
                                                        })}
                                                        className="edit-textarea"
                                                        placeholder="Kort beskrivelse..."
                                                        rows="2"
                                                        maxLength="150"
                                                    />
                                                    <small className="character-count">
                                                        {/* {editData.shortDescription.length}/150 characters */}
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="edit-form-row">
                                                <div className="edit-form-col">
                                                    <label>Full beskrivelse</label>
                                                    <textarea
                                                        value={editData.description}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            description: e.target.value
                                                        })}
                                                        className="edit-textarea"
                                                        placeholder="Detaljert beskrivelse..."
                                                        rows="4"
                                                    />
                                                </div>
                                            </div>

                                            <div className="edit-form-row">
                                                <div className="edit-form-col">
                                                    <label>Lydfil <span className="required">*</span></label>
                                                    <div className="audio-controls">
                                                        {editData.audioUrl && (
                                                            <>
                                                                <button 
                                                                    type="button"
                                                                    className={`audio-button norwegian ${playingStates[`${editingId}-norwegian`] ? 'playing' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handlePlayAudio(editingId, editData.audioUrl, 'norwegian');
                                                                    }}
                                                                >
                                                                    <i className={`fas ${playingStates[`${editingId}-norwegian`] ? 'fa-pause' : 'fa-play'}`}></i> 
                                                                    {playingStates[`${editingId}-norwegian`] ? 'Pause norsk' : 'Spill norsk'}
                                                                </button>
                                                                <button 
                                                                    type="button"
                                                                    className={`audio-button english ${playingStates[`${editingId}-english`] ? 'playing' : ''}`}
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        handlePlayAudio(editingId, editData.audioUrl, 'english');
                                                                    }}
                                                                >
                                                                    <i className={`fas ${playingStates[`${editingId}-english`] ? 'fa-pause' : 'fa-play'}`}></i>
                                                                    {playingStates[`${editingId}-english`] ? 'Pause engelsk' : 'Spill engelsk'}
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>

                                                    <MediaSelector 
                                                        onSelect={(url) => setEditData({
                                                            ...editData,
                                                            audioUrl: url
                                                        })}
                                                        selectedUrl={editData.audioUrl}
                                                    />
                                                    {!editData.audioUrl && !editData.videoUrl && (
                                                        <small className="error-text">Enten lydfil eller videofil er påkrevd</small>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="edit-form-row">
                                                <div className="edit-form-col">
                                                    <label>Videofil <span className="required">*</span></label>
                                                    <div className="video-preview">
                                                        {editData.videoUrl && (
                                                            <div className="video-container">
                                                                <video 
                                                                    controls 
                                                                    src={editData.videoUrl}
                                                                    className="preview-video"
                                                                >
                                                                    Nettleseren din støtter ikke video-taggen.
                                                                </video>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <MediaSelector 
                                                        onSelect={(url) => setEditData({
                                                            ...editData,
                                                            videoUrl: url
                                                        })}
                                                        selectedUrl={editData.videoUrl}
                                                        folder="video"
                                                        accept="video/*"
                                                    />
                                                </div>
                                            </div>

                                            <div className="edit-form-row">
                                                <div className="edit-form-col toggle-group">
                                                    <label className="toggle-label">
                                                        <span className="toggle-text">Publisert aktivitet</span>
                                                        <div className="toggle-switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={editData.isPublic}
                                                                onChange={(e) => setEditData({
                                                                    ...editData,
                                                                    isPublic: e.target.checked
                                                                })}
                                                            />
                                                            <span className="toggle-slider"></span>
                                                        </div>
                                                    </label>

                                                    <label className="toggle-label" style={{ marginLeft: '2rem' }}>
                                                        <span className="toggle-text">Gratis aktivitet</span>
                                                        <div className="toggle-switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={editData.isFree}
                                                                onChange={(e) => setEditData({
                                                                    ...editData,
                                                                    isFree: e.target.checked
                                                                })}
                                                            />
                                                            <span className="toggle-slider"></span>
                                                        </div>
                                                    </label>

                                                    <label className="toggle-label" style={{ marginLeft: '2rem' }}>
                                                        <span className="toggle-text">Gjentakende aktivitet</span>
                                                        <div className="toggle-switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={editData.isLoop}
                                                                onChange={(e) => setEditData({
                                                                    ...editData,
                                                                    isLoop: e.target.checked
                                                                })}
                                                            />
                                                            <span className="toggle-slider"></span>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="edit-form-row">
                                                <div className="edit-form-col">
                                                    <label>Stemmenivå</label>
                                                    <select
                                                        value={editData.voicingLevel}
                                                        onChange={(e) => setEditData({
                                                            ...editData,
                                                            voicingLevel: e.target.value
                                                        })}
                                                        className="edit-select"
                                                    >
                                                        <option value="no">Ingen stemme</option>
                                                        <option value="some">Litt stemme</option>
                                                        <option value="consistent">Konsistent stemme</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <h3>{activity.name}</h3>
                                            <p className="program-name">Program: {activity.program_name}</p>
                                            {activity.description && (
                                                <div className="activity-description-container">
                                                    <p className="activity-description">
                                                        {truncateText(activity.description, 120)}
                                                    </p>
                                                    {activity.description.length > 120 && (
                                                        <button 
                                                            className="show-more-button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEdit(activity);
                                                            }}
                                                        >
                                                            Vis mer
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            
                                            {(activity.norwegianURL || activity.norwegianUrl || activity.englishURL || activity.englishUrl) && (
                                                <div className="audio-controls">
                                                    {(activity.norwegianURL || activity.norwegianUrl) && (
                                                        <button 
                                                            className={`audio-button norwegian ${playingStates[`${activity.idactivity}-norwegian`] ? 'playing' : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePlayAudio(activity.idactivity, activity.norwegianURL || activity.norwegianUrl, 'norwegian');
                                                            }}
                                                        >
                                                            <i className={`fas ${playingStates[`${activity.idactivity}-norwegian`] ? 'fa-pause' : 'fa-play'}`}></i>
                                                            {playingStates[`${activity.idactivity}-norwegian`] ? '  Pause' : '  Spill'}
                                                        </button>
                                                    )}
                                                    {(activity.englishURL || activity.englishUrl) && (
                                                        <button 
                                                            className={`audio-button english ${playingStates[`${activity.idactivity}-english`] ? 'playing' : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handlePlayAudio(activity.idactivity, activity.englishURL || activity.englishUrl, 'english');
                                                            }}
                                                        >
                                                            <i className={`fas ${playingStates[`${activity.idactivity}-english`] ? 'fa-pause' : 'fa-play'}`}></i>
                                                            {playingStates[`${activity.idactivity}-english`] ? 'Pause engelsk' : 'Spill engelsk'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            <div className="activity-tags">
                                                {activity.tags && activity.tags.split(',').map((tag, index) => (
                                                    <span key={index} className="tag">
                                                        {tag.trim()}
                                                        <button
                                                            type="button"
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    const updatedTags = activity.tags
                                                                        .split(',')
                                                                        .filter((_, i) => i !== index)
                                                                        .join(',');
                                                                    
                                                                    await axios.put(
                                                                        `${process.env.REACT_APP_BACKEND_URL}/api/activities/${activity.idactivity}`,
                                                                        {
                                                                            name: activity.name,
                                                                            programId: activity.idprograms,
                                                                            description: activity.description,
                                                                            tags: updatedTags ? updatedTags.split(',') : []
                                                                        },
                                                                        { withCredentials: true }
                                                                    );
                                                                    
                                                                    // Use fetchActivities instead of fetchInitialData
                                                                    await fetchActivities();
                                                                } catch (error) {
                                                                    console.error('Error removing tag:', error);
                                                                    setError('Kunne ikke fjerne tag');
                                                                }
                                                            }}
                                                            className="tag-remove"
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                            {activity.level_name && (
                                                <p className="activity-detail">
                                                    <strong>Nivå:</strong> {activity.level_name}
                                                </p>
                                            )}
                                            {activity.step_name && (
                                                <p className="activity-detail">
                                                    <strong>Steg:</strong> {activity.step_name}
                                                </p>
                                            )}
                                            {activity.environment_name && (
                                                <p className="activity-detail">
                                                    <strong>Miljø:</strong> {activity.environment_name}
                                                </p>
                                            )}
                                            <p className="activity-detail">
                                                <strong>Status:</strong> {activity.isPublic === 1 ? 'Publisert' : 'Utkast'} | 
                                                {activity.isFree === 1 ? ' Gratis' : ' Pro'} |
                                                {activity.isLoop === 1 ? ' Gjentakende' : ' Ikke gjentakende'}
                                            </p>
                                            {activity.minutes && (
                                                <p className="activity-detail">
                                                    <strong>Varighet:</strong> {activity.minutes} minutter
                                                </p>
                                            )}
                                            <p className="activity-detail">
                                                <strong>Stemme:</strong> {activity.voicing_level === 'no' ? 'Ingen' : 
                                                                 activity.voicing_level === 'consistent' ? 'Konsistent' : 'Normal'}
                                            </p>
                                            {/* Add video preview in activity list view */}
                                        </>
                                    )}
                                </div>
                                <div className="list-item-right">
                                    <div className="list-item-actions">
                                        {editingId === activity.idactivity ? (
                                            <>
                                                <button 
                                                    className="save-button"
                                                    onClick={() => handleUpdate(activity.idactivity)}
                                                    disabled={!editData.audioUrl && !editData.videoUrl}
                                                >
                                                    <i className="fas fa-save"></i>
                                                </button>
                                                <button 
                                                    className="cancel-button"
                                                    onClick={() => setEditingId(null)}
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button 
                                                    className="edit-button"
                                                    onClick={() => handleEdit(activity)}
                                                >
                                                    <i className="fas fa-pencil-alt"></i>
                                                </button>
                                                <button 
                                                    className="delete-button"
                                                    onClick={() => {
                                                        setDeleteConfirm(activity);
                                                    }}
                                                >
                                                    <i className="fas fa-trash-alt"></i>
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    {(activity.videoURL || activity.videoUrl) && (
                                        <div className="video-preview">
                                            <div className="video-container">
                                                <video 
                                                    controls 
                                                    src={activity.videoURL || activity.videoUrl}
                                                    className="preview-video"
                                                >
                                                    Nettleseren din støtter ikke video-taggen.
                                                </video>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                        ))
                    ) : (
                        <div className="list-item">
                            <span>Ingen aktiviteter funnet</span>
                        </div>
                    )}
                </div>
            </div>
            {deleteConfirm && (
                <DeleteModal 
                    isOpen={!!deleteConfirm}
                    onClose={() => setDeleteConfirm(null)}
                    onConfirm={() => handleDelete(deleteConfirm.idactivity)}
                    itemName={`activity "${deleteConfirm.name}"`}
                />
            )}

      
        </div>
    );
} 